// config.ts

import * as yup from 'yup';

import { checkPersonName, checkWishes } from 'utils/textGuard';

import { formatStudyTime, studyScheduleCodes, studyTimeError, studyTimeToApi, type StudyTime } from 'utils/studyTime';

import { DEFAULT_SUBJECT } from './taskForm.store';

const WISHES_MAX = 2000;

/* ---------------- VALIDATION ---------------- */

export const taskValidationSchema = yup.object({
  selectedParticipants: yup.array(),

  studentType: yup.string().required('Выберите, кто будет заниматься'),

  grades: yup
    .array()
    .of(yup.string())
    .when('studentType', {
      is: 'school',
      then: (schema) => schema.min(1, 'Выберите хотя бы один класс'),
      otherwise: (schema) => schema,
    }),

  goal: yup.string().required('Выберите цель занятия'),

  teacherGender: yup.string().required('Выберите пол репетитора'),

  duration: yup.string().required('Выберите длительность'),

  locations: yup.array().min(1, 'Выберите место занятий'),

  // «Когда удобно заниматься?»: как в подборе. Пустое расписание можно пропустить.
  // Если день включен, время «С» и «До» по-прежнему обязательно и «До» позже «С».
  studyTime: yup.mixed().test('study-time', 'Проверьте время', function (value) {
    const message = studyTimeError(value as StudyTime | null, false);
    if (!message) return true;
    return this.createError({ message });
  }),

  comment: yup
    .string()
    .max(WISHES_MAX, `Не более ${WISHES_MAX} символов`)
    .test('wishes-sense', 'Проверьте пожелания', function (value) {
      const message = checkWishes(String(value || ''));
      if (!message) return true;
      return this.createError({ message });
    }),

  studentName: yup.string().test('name-sense', 'Проверьте имя', function (value) {
    const name = String(value || '').trim();
    if (!name) return true;
    const message = checkPersonName(name);
    if (!message) return true;
    return this.createError({ message });
  }),

  priceFrom: yup
    .number()
    .transform((v, o) => (o === '' || o == null ? undefined : v))
    .nullable()
    .test('price-from-min', 'Цена от не меньше 500 рублей в час', (value) => value == null || value >= 500)
    .test('price-from-required', 'Укажите цену от или цену до', function (value) {
      const { priceTo } = this.parent;
      if (!value && !priceTo) {
        return this.createError({ path: 'priceFrom' });
      }
      return true;
    })
    .test(
      'price-from-less',
      'Цена от не может быть больше цены до',
      function (value) {
        const { priceTo } = this.parent;
        if (!value || !priceTo) return true;
        return value <= priceTo;
      },
    ),

  priceTo: yup
    .number()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .test('price-to-min', 'Цена до не меньше 500 рублей в час', (value) => value == null || value >= 500)
    .test('price-to-required', 'Укажите цену от или цену до', function (value) {
      const { priceFrom } = this.parent;
      if (!value && !priceFrom) {
        return this.createError({ path: 'priceTo' });
      }
      return true;
    })
    .test('price-to-more', 'Цена до не может быть меньше цены от', function (value) {
      const { priceFrom } = this.parent;
      if (!value || !priceFrom) return true;
      return value >= priceFrom;
    }),

  trial: yup.string().required('Выберите вариант'),
});

/* ---------------- DISPLAY MAP ---------------- */

export const valueMap: Record<string, Record<string, string>> = {
  studentType: {
    school: 'Школьник',
    graduated: 'Студент',
  },

  teacherGender: {
    male: 'Мужчина',
    female: 'Женщина',
    any: 'Любой',
  },

  duration: {
    express: 'Экспресс-занятие',
    short: 'Краткосрочно',
    long: 'Длительно',
    later: 'Решу позже',
  },

  trial: {
    yes: 'Да',
    no: 'Нет',
  },
};

export const locationMap: Record<string, string> = {
  online: 'Онлайн',
  at_tutor: 'У репетитора',
  at_student: 'У меня',
  any: 'Неважно',
};

/* ---------------- FORMATTER ---------------- */

export function formatValue(key: string, values: Record<string, unknown>) {
  if (key === 'learnerType') {
    const value = values.studentType as string;
    if (!value) return 'Не заполнено';
    return valueMap.studentType?.[value] || String(value);
  }

  if (key === 'participants') {
    const list = (values.selectedParticipants as Array<Record<string, string>>) || [];
    if (!list.length) return 'Не заполнено';
    return list
      .map((child) => [child.name, child.surname].filter(Boolean).join(' '))
      .join(', ');
  }

  if (key === 'grades') {
    const grades = (values.grades as string[]) || [];
    if (!grades.length) return 'Не заполнено';
    return grades.map((g) => `${g} класс`).join(', ');
  }

  if (key === 'subjectGoal') {
    const goal = values.goalLabel;
    if (!goal) return 'Не заполнено';
    const subject =
      (values.subjectLabel as string | undefined) ||
      (values.categoryLabel as string | undefined) ||
      '';
    return subject ? `${subject}, ${goal}` : String(goal);
  }

  if (key === 'schedule') {
    return formatStudyTime(values.studyTime as StudyTime | null) || 'Не заполнено';
  }

  if (key === 'price') {
    if (!values.priceFrom && !values.priceTo) return 'Не заполнено';

    const fmt = (n: unknown) =>
      typeof n === 'number' ? new Intl.NumberFormat('ru-RU').format(n) : String(n);

    if (values.priceFrom && values.priceTo) {
      return `${fmt(values.priceFrom)} - ${fmt(values.priceTo)} руб/час`;
    }

    if (values.priceFrom) return `от ${fmt(values.priceFrom)} руб./час`;
    if (values.priceTo) return `до ${fmt(values.priceTo)} руб./час`;
  }

  const value = values[key];

  if (!value) return 'Не заполнено';

  if (Array.isArray(value)) {
    return value.map((v) => locationMap[v as string] || String(v)).join(', ');
  }

  if (valueMap[key]?.[value as string]) {
    return valueMap[key][value as string];
  }

  return String(value);
}

export const LOCATION_MAP: Record<string, string> = {
  Онлайн: 'online',
  'У репетитора': 'at_tutor',
  'У меня': 'at_student',
  Неважно: 'any',
};

export const mapTaskFormToRequest = (
  values: Record<string, unknown>,
  profile?: { registrant_type?: string; name?: string; surname?: string },
) => {
  const registrantType = profile?.registrant_type || 'self';
  const isSelf = registrantType !== 'parent';
  const grades = ((values.grades as string[]) || []).map((g) => Number(g));

  let studentName = String(values.studentName || '').trim();
  if (!studentName) {
    studentName = [profile?.name, isSelf ? profile?.surname : '']
      .filter(Boolean)
      .join(' ')
      .trim();
  }
  if (!studentName) {
    studentName = 'Ученик';
  }

  const studentType = (values.studentType as string) || (grades.length ? 'school' : 'graduated');

  const payload: Record<string, unknown> = {
    student_type: studentType,
    grades: studentType === 'school' ? grades : [],
    grade: studentType === 'school' && grades.length ? grades[0] : undefined,
    participants: [],

    subject_category: values.subject || DEFAULT_SUBJECT,

    student_name: studentName,
    self_study: isSelf,

    tutor_gender_preference: values.teacherGender || 'any',

    planned_duration: values.duration,

    wishes: values.comment ? String(values.comment).slice(0, WISHES_MAX) : undefined,

    price_from: values.priceFrom ? Number(values.priceFrom) : undefined,
    price_to: values.priceTo ? Number(values.priceTo) : undefined,

    trial_lesson_preference: values.trial,
  };

  if (values.locations && (values.locations as string[]).length) {
    payload.study_locations = (values.locations as string[])
      .map((loc) => LOCATION_MAP[loc])
      .filter(Boolean);
  }

  if (values.goal) {
    payload.subject_subcategory = values.goal;
  }

  // Удобное время: структурно (study_time) + старые коды для совместимости.
  const untouchedLegacy =
    Boolean(values.studyTimeOptional) && !studyTimeToApi(values.studyTime as StudyTime | null);
  if (values.studyTime !== undefined && !untouchedLegacy) {
    const studyTime = studyTimeToApi(values.studyTime as StudyTime | null);
    payload.study_time = studyTime;
    payload.study_schedule = studyScheduleCodes(studyTime);
  }

  return payload;
};
